import { useEffect, useState } from "react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, ShieldAlert, Upload } from "lucide-react";
import { cn } from "@/lib/utils";

interface FieldSite { id: string; name: string; latitude: number; longitude: number; radius_m: number; }
interface Member { student_id: string; students: { first_name: string; last_name: string; student_number: string | null } | null; }

interface Props {
  groupId: string;
  groupName: string;
  trigger?: React.ReactNode;
  onSaved?: () => void;
}

export function AdminOverrideDialog({ groupId, groupName, trigger, onSaved }: Props) {
  const { hasRole, isTopManagement } = useAuth();
  const canOverride =
    isTopManagement() || hasRole("head_academic") || hasRole("head_administration") || hasRole("it_incharge");

  const [open, setOpen] = useState(false);
  const [sites, setSites] = useState<FieldSite[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [studentId, setStudentId] = useState("");
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [status, setStatus] = useState("present");
  const [siteId, setSiteId] = useState<string>("__none__");
  const [siteNameOverride, setSiteNameOverride] = useState("");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [radius, setRadius] = useState("");
  const [justification, setJustification] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !groupId) return;
    (async () => {
      const [sitesRes, membersRes] = await Promise.all([
        supabase.from("field_sites").select("id,name,latitude,longitude,radius_m").eq("active", true).order("name"),
        supabase
          .from("field_group_members")
          .select("student_id, students(first_name,last_name,student_number)")
          .eq("group_id", groupId),
      ]);
      setSites((sitesRes.data ?? []) as any);
      setMembers((membersRes.data ?? []) as any);
    })();
  }, [open, groupId]);

  function reset() {
    setStudentId("");
    setDate(new Date());
    setStatus("present");
    setSiteId("__none__");
    setSiteNameOverride("");
    setLat(""); setLng(""); setRadius("");
    setJustification("");
    setFile(null);
  }

  async function handleSave() {
    if (!studentId) return toast.error("Select a student.");
    if (!date) return toast.error("Select a date.");
    if (justification.trim().length < 5) return toast.error("Justification must be at least 5 characters.");

    setSaving(true);
    try {
      let fileUrl: string | null = null;
      if (file) {
        const path = `${groupId}/${studentId}/${Date.now()}-${file.name}`;
        const { error: upErr } = await supabase.storage.from("rotation-overrides").upload(path, file);
        if (upErr) throw upErr;
        fileUrl = path;
      }

      const { error } = await supabase.rpc("admin_override_rotation_attendance" as any, {
        _field_group_id: groupId,
        _student_id: studentId,
        _attendance_date: format(date, "yyyy-MM-dd"),
        _status: status,
        _justification: justification.trim(),
        _site_id: siteId === "__none__" ? null : siteId,
        _site_name_override: siteNameOverride || null,
        _lat: lat ? Number(lat) : null,
        _lng: lng ? Number(lng) : null,
        _radius_m: radius ? Number(radius) : null,
        _justification_file_url: fileUrl,
      });
      if (error) throw error;
      toast.success("Override saved");
      onSaved?.();
      setOpen(false);
      reset();
    } catch (e: any) {
      toast.error(e.message ?? "Failed to save override");
    } finally {
      setSaving(false);
    }
  }

  if (!canOverride) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="sm">
            <ShieldAlert className="w-4 h-4 mr-1" /> Admin Override
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-destructive" /> Admin Override — {groupName}
          </DialogTitle>
          <DialogDescription>
            Backfill or correct out-of-fence Rotation scans. A justification is required and the action is permanently audit-logged.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Student</Label>
              <Select value={studentId} onValueChange={setStudentId}>
                <SelectTrigger><SelectValue placeholder="Select student" /></SelectTrigger>
                <SelectContent>
                  {members.map((m) => (
                    <SelectItem key={m.student_id} value={m.student_id}>
                      {m.students ? `${m.students.first_name} ${m.students.last_name}` : m.student_id}
                      {m.students?.student_number ? ` · ${m.students.student_number}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="present">Present</SelectItem>
                  <SelectItem value="absent">Absent</SelectItem>
                  <SelectItem value="late">Late</SelectItem>
                  <SelectItem value="excused">Excused</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs">Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className={cn("w-full justify-start font-normal", !date && "text-muted-foreground")}>
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, "PPP") : "Pick date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={date} onSelect={setDate} initialFocus className="pointer-events-auto" />
              </PopoverContent>
            </Popover>
          </div>

          <div>
            <Label className="text-xs">Correct site</Label>
            <Select value={siteId} onValueChange={setSiteId}>
              <SelectTrigger><SelectValue placeholder="Select site" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— None (use ad-hoc below) —</SelectItem>
                {sites.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-3">
              <Label className="text-xs">Ad-hoc site name (optional)</Label>
              <Input value={siteNameOverride} onChange={(e) => setSiteNameOverride(e.target.value)} placeholder="e.g. Mwananyamala Hospital — Ward B" />
            </div>
            <div>
              <Label className="text-xs">Latitude</Label>
              <Input value={lat} onChange={(e) => setLat(e.target.value)} placeholder="-6.7924" />
            </div>
            <div>
              <Label className="text-xs">Longitude</Label>
              <Input value={lng} onChange={(e) => setLng(e.target.value)} placeholder="39.2083" />
            </div>
            <div>
              <Label className="text-xs">Radius (m)</Label>
              <Input value={radius} onChange={(e) => setRadius(e.target.value)} placeholder="150" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Justification *</Label>
            <Textarea
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              placeholder="Explain why this override is needed (e.g. GPS failure, late arrival at correct site, supervisor confirmation…)"
              rows={3}
            />
          </div>

          <div>
            <Label className="text-xs flex items-center gap-1"><Upload className="w-3 h-3" /> Supporting file (optional)</Label>
            <Input type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            {file && <p className="text-[11px] text-muted-foreground mt-1">{file.name}</p>}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save override"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
